import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Tracks an element's rendered height, for layout that has to react to a sibling's size.
 *
 * Returns a callback ref rather than an object ref on purpose, so it copes with the measured
 * element being conditionally rendered: attaching re-observes, detaching resets the height to 0.
 */
export const useElementHeight = <T extends HTMLElement>() => {
  const [height, setHeight] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect()

    if (!node) {
      observerRef.current = null
      setHeight(0)
      return
    }

    const observer = new ResizeObserver((entries) => {
      setHeight(entries[entries.length - 1]?.contentRect.height ?? 0)
    })
    observer.observe(node)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return { ref, height }
}
